// frontend/src/components/widgets/WeatherWidget.js - WITH PRESET SYSTEM
import React, { useState, useEffect } from 'react';
import { 
  Cloud, CloudRain, CloudSnow, Sun, CloudDrizzle, Wind, 
  Droplets, Eye, Gauge, MapPin, Calendar, Clock, Sunrise, Sunset 
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

// 🎯 PRESET DEFINITIONS
const DISPLAY_PRESETS = {
  minimal: {
    label: 'weatherConfig.presets.minimal',
    description: 'weatherConfig.presets.minimalDesc',
    options: {
      showTemperature: true,
      showFeelsLike: false,
      showHumidity: false,
      showWind: false,
      showPressure: false,
      showVisibility: false,
      animatedIcons: true
    }
  },
  sidebar: {  // ✅ NEU - Direkt nach minimal
    label: 'weatherConfig.presets.sidebar',
    description: 'weatherConfig.presets.sidebarDesc',
    options: {
      showTemperature: true,
      showFeelsLike: false,
      showHumidity: false,
      showWind: false,
      showPressure: false,
      showVisibility: false,
      showForecast: false,  // ✅ NEU: Kein Forecast
      animatedIcons: false  // Kein Icon für mehr Platz
    }
  },
  standard: {
    label: 'weatherConfig.presets.standard',
    description: 'weatherConfig.presets.standardDesc',
    options: {
      showTemperature: true,
      showFeelsLike: true,
      showHumidity: true,
      showWind: true,
      showPressure: false,
      showVisibility: false,
      animatedIcons: true
    }
  },
  extended: {
    label: 'weatherConfig.presets.extended',
    description: 'weatherConfig.presets.extendedDesc',
    options: {
      showTemperature: true,
      showFeelsLike: true,
      showHumidity: true,
      showWind: true,
      showPressure: true,
      showVisibility: true,
      animatedIcons: true
    }
  },
  complete: {
    label: 'weatherConfig.presets.complete',
    description: 'weatherConfig.presets.completeDesc',
    options: {
      showTemperature: true,
      showFeelsLike: true,
      showHumidity: true,
      showWind: true,
      showPressure: true,
      showVisibility: true,
      animatedIcons: true
    }
  }
};

const WeatherWidget = ({ config, screenSize }) => {
  const { t, i18n } = useTranslation();
  
  // 🎯 Responsive Multipliers
  const fontMultiplier = screenSize?.fontSizeMultiplier || 1.0;
  const spacingMultiplier = screenSize?.spacingMultiplier || 1.0;
    const layoutHint = screenSize?.layoutHint || 'default';  // ✅ NEU

    console.log('🌦️ WeatherWidget:', { fontMultiplier, spacingMultiplier, layoutHint });

  const [weatherData, setWeatherData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // 🎯 GET EFFECTIVE DISPLAY OPTIONS
  // Priority: config.displayOptions > preset > default standard
  const getEffectiveDisplayOptions = () => {
    // If displayOptions exist in config, use them (backward compatibility)
    if (config.displayOptions) {
      return config.displayOptions;
    }
    
    // Otherwise use preset
    const preset = config.displayPreset || 'standard';
    return DISPLAY_PRESETS[preset]?.options || DISPLAY_PRESETS.standard.options;
  };

  const effectiveDisplayOptions = getEffectiveDisplayOptions();

  // Get layout and theme from config
  const layout = config.layout || 'cards';
  const theme = config.theme || 'dynamic';

  // 🎯 RESPONSIVE BREAKPOINTS - Structural layout changes based on screen size
  const getResponsiveConfig = () => {
    const breakpoint = screenSize?.breakpoint || 'FHD';
    
    // BASE SIZES optimiert für jeden Breakpoint
    switch (breakpoint) {
      case 'SM': // 640px - 768px
      case 'MD': // 768px - 1024px
        return {
          // Sehr kompaktes Layout
          containerPadding: 15,
          sectionGap: 25,
          
          // Große Schriften relativ zum kleinen Screen
          locationText: 28,
          locationIcon: 32,
          mainIcon: 100,
          mainTemp: 80,
          condition: 24,
          feelsLike: 16,
          
          // Details kompakt, 3 Spalten
          detailColumns: 3,
          detailIcon: 28,
          detailValue: 26,
          detailLabel: 13,
          detailPadding: 16,
          detailGap: 10,
          
          // Forecast kompakt, 3 Spalten
          forecastColumns: 5,
          forecastTitle: 18,
          forecastDay: 14,
          forecastEmoji: 36,
          forecastTempHigh: 20,
          forecastTempLow: 16,
          forecastPadding: 20,
          forecastGap: 12
        };
        
      case 'HD': // 1024px - 1366px (wie dein 1280x631)
        return {
          // Kompaktes Layout aber mehr Platz
          containerPadding: 25,
          sectionGap: 35,
          
          // Gute Lesbarkeit
          locationText: 34,
          locationIcon: 38,
          mainIcon: 120,
          mainTemp: 100,
          condition: 28,
          feelsLike: 18,
          
          // Details 3 Spalten
          detailColumns: 3,
          detailIcon: 32,
          detailValue: 30,
          detailLabel: 14,
          detailPadding: 20,
          detailGap: 14,
          
          // Forecast 4 Spalten
          forecastColumns: 5,
          forecastTitle: 20,
          forecastDay: 15,
          forecastEmoji: 42,
          forecastTempHigh: 22,
          forecastTempLow: 18,
          forecastPadding: 25,
          forecastGap: 16
        };
        
      case 'FHD': // 1366px - 1920px
        return {
          // Standard Layout
          containerPadding: 50,
          sectionGap: 50,
          
          locationText: 40,
          locationIcon: 44,
          mainIcon: 160,
          mainTemp: 130,
          condition: 36,
          feelsLike: 22,
          
          // Details 2 Spalten
          detailColumns: 2,
          detailIcon: 36,
          detailValue: 32,
          detailLabel: 16,
          detailPadding: 28,
          detailGap: 20,
          
          // Forecast 5 Spalten
          forecastColumns: 5,
          forecastTitle: 24,
          forecastDay: 18,
          forecastEmoji: 56,
          forecastTempHigh: 26,
          forecastTempLow: 22,
          forecastPadding: 35,
          forecastGap: 20
        };
        
      case 'QHD': // 1920px - 2560px
        return {
          // Großzügiges Layout
          containerPadding: 70,
          sectionGap: 70,
          
          locationText: 50,
          locationIcon: 54,
          mainIcon: 200,
          mainTemp: 170,
          condition: 44,
          feelsLike: 28,
          
          detailColumns: 2,
          detailIcon: 44,
          detailValue: 40,
          detailLabel: 20,
          detailPadding: 36,
          detailGap: 26,
          
          forecastColumns: 5,
          forecastTitle: 30,
          forecastDay: 22,
          forecastEmoji: 68,
          forecastTempHigh: 32,
          forecastTempLow: 28,
          forecastPadding: 45,
          forecastGap: 26
        };
        
      case '4K': // 2560px+
        return {
          // Sehr großzügiges Layout
          containerPadding: 100,
          sectionGap: 100,
          
          locationText: 70,
          locationIcon: 74,
          mainIcon: 280,
          mainTemp: 240,
          condition: 60,
          feelsLike: 38,
          
          detailColumns: 3,
          detailIcon: 60,
          detailValue: 56,
          detailLabel: 28,
          detailPadding: 50,
          detailGap: 36,
          
          forecastColumns: 5,
          forecastTitle: 42,
          forecastDay: 30,
          forecastEmoji: 90,
          forecastTempHigh: 44,
          forecastTempLow: 38,
          forecastPadding: 60,
          forecastGap: 36
        };
        
      default:
  // Fallback zu FHD - direkt Werte zurückgeben statt rekursiver Aufruf
  return {
    containerPadding: 50,
    sectionGap: 50,
    locationText: 40,
    locationIcon: 44,
    mainIcon: 160,
    mainTemp: 130,
    condition: 36,
    feelsLike: 22,
    detailColumns: 2,
    detailIcon: 36,
    detailValue: 32,
    detailLabel: 16,
    detailPadding: 28,
    detailGap: 20,
    forecastColumns: 5,
    forecastTitle: 24,
    forecastDay: 18,
    forecastEmoji: 56,
    forecastTempHigh: 26,
    forecastTempLow: 22,
    forecastPadding: 35,
    forecastGap: 20
    };
    }
  };

  const RESPONSIVE = getResponsiveConfig();

  useEffect(() => {
    fetchWeather();
    const weatherInterval = setInterval(fetchWeather, config.updateInterval * 1000);
    const timeInterval = setInterval(() => setCurrentTime(new Date()), 1000);

    return () => {
      clearInterval(weatherInterval);
      clearInterval(timeInterval);
    };
  }, [config]);

  const fetchWeather = async () => {
  try {
    // 🛡️ CONFIG VALIDATION
    if (!config || !config.location || config.location.trim() === '') {
      console.warn('⚠️ WeatherWidget: No location configured, using demo data');
      setWeatherData(getMockWeatherData('Demo')); // WICHTIG: Mock-Daten setzen!
      setLoading(false);
      return;
    }

    // Validate and sanitize config
    const sanitizedConfig = {
      location: config.location.trim(),
      units: config.units && ['metric', 'imperial'].includes(config.units) ? config.units : 'metric',
      updateInterval: config.updateInterval >= 300 ? config.updateInterval : 600
    };

    const apiKey = process.env.REACT_APP_OPENWEATHER_API_KEY || 'demo';
    
    if (apiKey === 'demo') {
      console.log('☀️ Using demo weather data');
      setWeatherData(getMockWeatherData(sanitizedConfig.location));
      setLoading(false);
      return;
    }

    const weatherUrl = `https://api.openweathermap.org/data/2.5/weather?q=${encodeURIComponent(sanitizedConfig.location)}&units=${sanitizedConfig.units}&appid=${apiKey}`;
    const weatherResponse = await fetch(weatherUrl);
    
    if (!weatherResponse.ok) {
      throw new Error('Failed to fetch weather data');
    }

    const data = await weatherResponse.json();
    
    const forecastUrl = `https://api.openweathermap.org/data/2.5/forecast?q=${encodeURIComponent(sanitizedConfig.location)}&units=${sanitizedConfig.units}&appid=${apiKey}`;
    const forecastResponse = await fetch(forecastUrl);
    
    let forecastData = [];
    if (forecastResponse.ok) {
      const forecast = await forecastResponse.json();
      forecastData = processForecastData(forecast.list);
    }
    
    setWeatherData({
      temperature: Math.round(data.main.temp),
      feelsLike: Math.round(data.main.feels_like),
      condition: data.weather[0].main,
      conditionCode: getConditionCode(data.weather[0].id),
      humidity: data.main.humidity,
      windSpeed: Math.round(data.wind.speed * (sanitizedConfig.units === 'metric' ? 3.6 : 1)),
      windDirection: getWindDirection(data.wind.deg),
      pressure: data.main.pressure,
      visibility: Math.round(data.visibility / 1000),
      sunrise: new Date(data.sys.sunrise * 1000).toLocaleTimeString(getLocale(), { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      }),
      sunset: new Date(data.sys.sunset * 1000).toLocaleTimeString(getLocale(), { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      }),
      forecast: forecastData
    });
    
    setLoading(false);
    setError(null);
  } catch (err) {
    console.error('Error fetching weather:', err);
    setError('Failed to fetch weather data');
    setWeatherData(getMockWeatherData(config.location || 'Demo')); // WICHTIG: Mock-Daten im Error-Fall!
    setLoading(false);
  }
};

  const getMockWeatherData = (location) => {
    return {
      temperature: 9,
      feelsLike: 6,
      condition: 'Clouds',
      conditionCode: 'Clouds',
      humidity: 85,
      windSpeed: 12,
      windDirection: 'W',
      pressure: 1013,
      visibility: 10,
      sunrise: '07:28',
      sunset: '16:46',
      forecast: [
        { day: 'Mo', temp: 12, high: 14, low: 8, condition: 'partly-cloudy', icon: '🌤️' },
        { day: 'Di', temp: 10, high: 12, low: 6, condition: 'cloudy', icon: '☁️' },
        { day: 'Mi', temp: 8, high: 10, low: 5, condition: 'rainy', icon: '🌧️' },
        { day: 'Do', temp: 7, high: 9, low: 4, condition: 'rainy', icon: '⛈️' },
        { day: 'Fr', temp: 9, high: 11, low: 6, condition: 'cloudy', icon: '☁️' }
      ]
    };
  };

  const getConditionCode = (weatherId) => {
    if (weatherId >= 200 && weatherId < 300) return 'rainy';
    if (weatherId >= 300 && weatherId < 400) return 'drizzle';
    if (weatherId >= 500 && weatherId < 600) return 'rainy';
    if (weatherId >= 600 && weatherId < 700) return 'snowy';
    if (weatherId >= 700 && weatherId < 800) return 'cloudy';
    if (weatherId === 800) return 'sunny';
    if (weatherId === 801) return 'partly-cloudy';
    if (weatherId > 801) return 'cloudy';
    return 'partly-cloudy';
  };

  const getWindDirection = (deg) => {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(deg / 45) % 8;
    return directions[index];
  };

  const processForecastData = (forecastList) => {
    const dailyForecasts = {};
    const locale = getLocale();
    
    forecastList.forEach(item => {
      const date = new Date(item.dt * 1000);
      const dateKey = date.toLocaleDateString(locale, { weekday: 'short' });
      const hour = date.getHours();
      
      if (hour >= 11 && hour <= 14) {
        if (!dailyForecasts[dateKey] || Math.abs(hour - 12) < Math.abs(dailyForecasts[dateKey].hour - 12)) {
          dailyForecasts[dateKey] = {
            day: dateKey,
            temp: Math.round(item.main.temp),
            high: Math.round(item.main.temp_max),
            low: Math.round(item.main.temp_min),
            condition: getConditionCode(item.weather[0].id),
            icon: getWeatherEmoji(item.weather[0].main),
            hour: hour
          };
        }
      }
    });
    
    return Object.values(dailyForecasts).slice(0, 5);
  };

  const getWeatherEmoji = (condition) => {
    const emojis = {
      'Clear': '☀️',
      'Clouds': '☁️',
      'Rain': '🌧️',
      'Drizzle': '🌦️',
      'Thunderstorm': '⛈️',
      'Snow': '🌨️',
      'Mist': '🌫️',
      'Fog': '🌫️',
      'Haze': '🌫️'
    };
    return emojis[condition] || '🌤️';
  };

  const getLocale = () => {
    const langMap = {
      'de': 'de-CH',
      'en': 'en-US',
      'fr': 'fr-CH',
      'it': 'it-CH'
    };
    return langMap[i18n.language] || 'de-CH';
  };

  const getResponsiveIconSize = (baseSize) => {
    return Math.round(baseSize * fontMultiplier);
  };

  const translateWeatherCondition = (condition) => {
    const translations = {
      'de': {
        'Clear': 'Klar',
        'Clouds': 'Bewölkt',
        'Rain': 'Regen',
        'Drizzle': 'Nieselregen',
        'Thunderstorm': 'Gewitter',
        'Snow': 'Schnee',
        'Mist': 'Nebel',
        'Smoke': 'Rauch',
        'Haze': 'Dunst',
        'Dust': 'Staub',
        'Fog': 'Nebel',
        'Sand': 'Sand',
        'Ash': 'Asche',
        'Squall': 'Böe',
        'Tornado': 'Tornado',
        'Partly cloudy': 'Teilweise bewölkt',
        'Overcast': 'Bedeckt'
      },
      'en': {
        'Clear': 'Clear',
        'Clouds': 'Cloudy',
        'Rain': 'Rain',
        'Drizzle': 'Drizzle',
        'Thunderstorm': 'Thunderstorm',
        'Snow': 'Snow',
        'Mist': 'Mist',
        'Smoke': 'Smoke',
        'Haze': 'Haze',
        'Dust': 'Dust',
        'Fog': 'Fog',
        'Sand': 'Sand',
        'Ash': 'Ash',
        'Squall': 'Squall',
        'Tornado': 'Tornado',
        'Partly cloudy': 'Partly cloudy',
        'Overcast': 'Overcast'
      },
      'fr': {
        'Clear': 'Clair',
        'Clouds': 'Nuageux',
        'Rain': 'Pluie',
        'Drizzle': 'Bruine',
        'Thunderstorm': 'Orage',
        'Snow': 'Neige',
        'Mist': 'Brume',
        'Smoke': 'Fumée',
        'Haze': 'Brume',
        'Dust': 'Poussière',
        'Fog': 'Brouillard',
        'Sand': 'Sable',
        'Ash': 'Cendre',
        'Squall': 'Grain',
        'Tornado': 'Tornade',
        'Partly cloudy': 'Partiellement nuageux',
        'Overcast': 'Couvert'
      },
      'it': {
        'Clear': 'Sereno',
        'Clouds': 'Nuvoloso',
        'Rain': 'Pioggia',
        'Drizzle': 'Pioggerella',
        'Thunderstorm': 'Temporale',
        'Snow': 'Neve',
        'Mist': 'Foschia',
        'Smoke': 'Fumo',
        'Haze': 'Foschia',
        'Dust': 'Polvere',
        'Fog': 'Nebbia',
        'Sand': 'Sabbia',
        'Ash': 'Cenere',
        'Squall': 'Burrasca',
        'Tornado': 'Tornado',
        'Partly cloudy': 'Parzialmente nuvoloso',
        'Overcast': 'Coperto'
      }
    };
    
    const lang = i18n.language || 'de';
    return translations[lang]?.[condition] || condition;
  };

  const getThemeStyles = () => {
    const themes = {
      modern: {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        textColor: 'text-white',
        cardBg: 'rgba(255, 255, 255, 0.15)',
        shadowColor: 'rgba(0, 0, 0, 0.3)'
      },
      classic: {
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
        textColor: 'text-white',
        cardBg: 'rgba(255, 255, 255, 0.2)',
        shadowColor: 'rgba(0, 0, 0, 0.5)'
      },
      minimal: {
        background: '#ffffff',
        textColor: 'text-gray-900',
        cardBg: 'rgba(0, 0, 0, 0.05)',
        shadowColor: 'rgba(0, 0, 0, 0.1)'
      },
      dark: {
        background: '#000000',
        textColor: 'text-white',
        cardBg: 'rgba(255, 255, 255, 0.1)',
        shadowColor: 'rgba(255, 255, 255, 0.1)'
      },
      dynamic: {
        background: getWeatherGradient(weatherData?.conditionCode || 'sunny'),
        textColor: 'text-white',
        cardBg: 'rgba(255, 255, 255, 0.2)',
        shadowColor: 'rgba(0, 0, 0, 0.3)'
      },
      weather: {
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        textColor: 'text-white',
        cardBg: 'rgba(255, 255, 255, 0.15)',
        shadowColor: 'rgba(0, 0, 0, 0.3)'
      }
    };
    return themes[theme] || themes.dynamic;
  };

  const getWeatherGradient = (condition) => {
    const gradients = {
      'sunny': 'linear-gradient(135deg, #FDB813 0%, #FF8C00 100%)',
      'clear': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'Clear': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'partly-cloudy': 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'Clouds': 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #7e8ba3 100%)',
      'cloudy': 'linear-gradient(135deg, #434343 0%, #000000 100%)',
      'rainy': 'linear-gradient(135deg, #2E3192 0%, #1BFFFF 100%)',
      'Rain': 'linear-gradient(135deg, #2E3192 0%, #1BFFFF 100%)',
      'snowy': 'linear-gradient(135deg, #E0EAFC 0%, #CFDEF3 100%)',
      'Snow': 'linear-gradient(135deg, #E0EAFC 0%, #CFDEF3 100%)',
      'drizzle': 'linear-gradient(135deg, #89F7FE 0%, #66A6FF 100%)',
      'Drizzle': 'linear-gradient(135deg, #89F7FE 0%, #66A6FF 100%)'
    };
    return gradients[condition] || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
  };

  const getWeatherIcon = (condition, size = 120) => {
    const responsiveSize = getResponsiveIconSize(size);
    const iconProps = { 
      size: responsiveSize, 
      strokeWidth: 1.5,
      className: 'drop-shadow-2xl'
    };

    switch (condition) {
      case 'sunny':
      case 'clear':
      case 'Clear':
        return <Sun {...iconProps} className="text-yellow-300 animate-pulse" />;
      case 'partly-cloudy':
      case 'Clouds':
        return <Cloud {...iconProps} className="text-white" />;
      case 'cloudy':
        return <Cloud {...iconProps} className="text-gray-300" />;
      case 'rainy':
      case 'Rain':
        return <CloudRain {...iconProps} className="text-blue-300" />;
      case 'snowy':
      case 'Snow':
        return <CloudSnow {...iconProps} className="text-blue-100" />;
      case 'drizzle':
      case 'Drizzle':
        return <CloudDrizzle {...iconProps} className="text-blue-200" />;
      default:
        return <Cloud {...iconProps} className="text-white" />;
    }
  };

  if (loading) {
    return (
      <div 
        className="w-full h-full flex items-center justify-center"
        style={{ background: getThemeStyles().background }}
      >
        <div className={`text-center ${getThemeStyles().textColor}`}>
          <Cloud size={getResponsiveIconSize(64)} className="animate-pulse mx-auto mb-4" />
          <p style={{ fontSize: `${21 * fontMultiplier}px` }}>{t('weather.loading')}</p>
        </div>
      </div>
    );
  }

  if (layout === 'dashboard') {
    return renderDashboardLayout();
  } else {
    return renderCardsLayout();
  }

  function renderCardsLayout() {
    const themeStyles = getThemeStyles();

    return (
      <div 
        className={`w-full h-full flex ${layoutHint === 'sidebar' ? 'items-start justify-start' : 'items-center justify-center'} ${themeStyles.textColor} relative overflow-hidden`}
        style={{ 
          background: themeStyles.background,
          fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
          minHeight: '100%',
          padding: `${8 * spacingMultiplier}px`
        }}
      >
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.05) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.05) 0%, transparent 50%)
            `,
            animation: 'float 20s ease-in-out infinite'
          }}
        />

        <div 
          className="relative z-10 w-full"
          style={{
            maxWidth: '95%',
            background: 'rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3), inset 0 1px 0 0 rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            animation: 'fadeIn 0.8s ease-out',
            padding: `${RESPONSIVE.containerPadding * spacingMultiplier}px`,
            borderRadius: `${30 * spacingMultiplier}px`,
            maxHeight: '95vh',
            overflowY: 'auto'
          }}
        >
          
          {/* Location Header */}
          <div 
            className="flex items-center justify-center"
            style={{ 
              fontSize: `${RESPONSIVE.locationText}px`,
              fontWeight: '300',
              letterSpacing: '1px',
              color: 'rgba(255, 255, 255, 0.95)',
              marginBottom: `${20 * spacingMultiplier}px`,
              gap: `${12 * spacingMultiplier}px`
            }}
          >
            <MapPin size={RESPONSIVE.locationIcon} style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))' }} />
            <span>{config.location}</span>
          </div>

          {/* Main Content - 2 Columns */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: `${RESPONSIVE.sectionGap * spacingMultiplier}px`, 
            marginBottom: `${RESPONSIVE.sectionGap * spacingMultiplier}px` 
          }}>
            
            {/* LEFT: Current Weather */}
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
              
              {effectiveDisplayOptions.animatedIcons && (
                <div 
                  style={{ 
                    marginBottom: `${15 * spacingMultiplier}px`,
                    filter: 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.3))',
                    animation: 'pulse 4s ease-in-out infinite'
                  }}
                >
                  {getWeatherIcon(weatherData.conditionCode, RESPONSIVE.mainIcon)}
                </div>
              )}

              {effectiveDisplayOptions.showTemperature && (
                <div 
                  style={{ 
                    fontSize: `${RESPONSIVE.mainTemp}px`,
                    fontWeight: '200',
                    color: '#ffffff',
                    lineHeight: '1',
                    marginBottom: `${15 * spacingMultiplier}px`,
                    textShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '-3px'
                  }}
                >
                  {Math.round(weatherData.temperature)}°
                </div>
              )}

              <div 
                style={{ 
                  fontSize: `${RESPONSIVE.condition}px`,
                  color: 'rgba(255, 255, 255, 0.95)',
                  marginBottom: `${12 * spacingMultiplier}px`,
                  fontWeight: '300',
                  letterSpacing: '0.5px'
                }}
              >
                {translateWeatherCondition(weatherData.condition)}
              </div>

              {effectiveDisplayOptions.showFeelsLike && (
                <div 
                  style={{ 
                    fontSize: `${RESPONSIVE.feelsLike}px`,
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontWeight: '300'
                  }}
                >
                  {t('weather.feelsLike', { temp: weatherData.feelsLike })}
                </div>
              )}

            </div>

            {/* RIGHT: Weather Details Grid */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: `repeat(${RESPONSIVE.detailColumns}, 1fr)`, 
              gap: `${RESPONSIVE.detailGap * spacingMultiplier}px` 
            }}>
              
              {effectiveDisplayOptions.showHumidity && (
                <DetailCard
                  icon={<Droplets size={RESPONSIVE.detailIcon} />}
                  value={`${weatherData.humidity}%`}
                  label={t('weather.humidity')}
                  valueSize={RESPONSIVE.detailValue}
                  labelSize={RESPONSIVE.detailLabel}
                  padding={RESPONSIVE.detailPadding}
                  fontMultiplier={1}
                  spacingMultiplier={spacingMultiplier}
                />
              )}

              {effectiveDisplayOptions.showWind && (
                <DetailCard
                  icon={<Wind size={RESPONSIVE.detailIcon} />}
                  value={`${weatherData.windSpeed} km/h`}
                  label={t('weather.wind')}
                  valueSize={RESPONSIVE.detailValue}
                  labelSize={RESPONSIVE.detailLabel}
                  padding={RESPONSIVE.detailPadding}
                  fontMultiplier={1}
                  spacingMultiplier={spacingMultiplier}
                />
              )}

              {effectiveDisplayOptions.showPressure && (
                <DetailCard
                  icon={<Gauge size={RESPONSIVE.detailIcon} />}
                  value={`${weatherData.pressure} hPa`}
                  label={t('weather.pressure')}
                  valueSize={RESPONSIVE.detailValue}
                  labelSize={RESPONSIVE.detailLabel}
                  padding={RESPONSIVE.detailPadding}
                  fontMultiplier={1}
                  spacingMultiplier={spacingMultiplier}
                />
              )}

              {effectiveDisplayOptions.showVisibility && (
                <DetailCard
                  icon={<Eye size={RESPONSIVE.detailIcon} />}
                  value={`${weatherData.visibility} km`}
                  label={t('weather.visibility')}
                  valueSize={RESPONSIVE.detailValue}
                  labelSize={RESPONSIVE.detailLabel}
                  padding={RESPONSIVE.detailPadding}
                  fontMultiplier={1}
                  spacingMultiplier={spacingMultiplier}
                />
              )}

              <DetailCard
                icon={<Sunrise size={RESPONSIVE.detailIcon} />}
                value={weatherData.sunrise}
                label={t('weather.sunrise')}
                valueSize={RESPONSIVE.detailValue}
                labelSize={RESPONSIVE.detailLabel}
                padding={RESPONSIVE.detailPadding}
                fontMultiplier={1}
                spacingMultiplier={spacingMultiplier}
              />

              <DetailCard
                icon={<Sunset size={RESPONSIVE.detailIcon} />}
                value={weatherData.sunset}
                label={t('weather.sunset')}
                valueSize={RESPONSIVE.detailValue}
                labelSize={RESPONSIVE.detailLabel}
                padding={RESPONSIVE.detailPadding}
                fontMultiplier={1}
                spacingMultiplier={spacingMultiplier}
              />

            </div>
          </div>

          {/* 5-Day Forecast */}
          {config.showForecast && weatherData.forecast && weatherData.forecast.length > 0 && (
            <div 
              style={{ 
                background: 'rgba(255, 255, 255, 0.06)',
                backdropFilter: 'blur(10px)',
                borderRadius: `${24 * spacingMultiplier}px`,
                padding: `${RESPONSIVE.forecastPadding * spacingMultiplier}px`,
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <div 
                style={{ 
                  fontSize: `${RESPONSIVE.forecastTitle}px`,
                  color: 'rgba(255, 255, 255, 0.9)',
                  marginBottom: `${20 * spacingMultiplier}px`,
                  fontWeight: '300',
                  letterSpacing: '0.5px'
                }}
              >
                {t('weather.forecast')}
              </div>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: `repeat(${RESPONSIVE.forecastColumns}, 1fr)`, 
                gap: `${RESPONSIVE.forecastGap * spacingMultiplier}px` 
              }}>
                {weatherData.forecast.slice(0, RESPONSIVE.forecastColumns).map((day, index) => (
                  <ForecastCard
                    key={index}
                    day={day}
                    daySize={RESPONSIVE.forecastDay}
                    emojiSize={RESPONSIVE.forecastEmoji}
                    tempSize={RESPONSIVE.forecastTempHigh}
                    tempLowSize={RESPONSIVE.forecastTempLow}
                    fontMultiplier={1}
                    spacingMultiplier={spacingMultiplier}
                  />
                ))}
              </div>
            </div>
          )}

        </div>

        <style>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }

          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }

          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
        `}</style>
      </div>
    );
  }

  function renderDashboardLayout() {
    const themeStyles = getThemeStyles();

    // Dashboard verwendet größere Werte für Split-Layout
    const DASH = {
      ...RESPONSIVE,
      mainIcon: RESPONSIVE.mainIcon * 1.5,
      mainTemp: RESPONSIVE.mainTemp * 1.5,
      condition: RESPONSIVE.condition * 1.3,
      feelsLike: RESPONSIVE.feelsLike * 1.3,
      locationText: RESPONSIVE.locationText * 1.3,
      locationIcon: RESPONSIVE.locationIcon * 1.3,
    };

    return (
      <div 
        className={`w-full h-full flex items-center justify-center ${themeStyles.textColor} relative overflow-hidden`}
        style={{ 
          background: themeStyles.background,
          fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
          minHeight: '100vh',
          padding: `${8 * spacingMultiplier}px`
        }}
      >
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.05) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.05) 0%, transparent 50%)
            `,
            animation: 'float 20s ease-in-out infinite'
          }}
        />

        <div 
          className="relative z-10 w-full"
          style={{
            maxWidth: '95%',
            background: 'rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3), inset 0 1px 0 0 rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            animation: 'fadeIn 0.8s ease-out',
            padding: `${20 * spacingMultiplier}px ${RESPONSIVE.containerPadding * spacingMultiplier}px ${RESPONSIVE.containerPadding * spacingMultiplier}px`,
            borderRadius: `${30 * spacingMultiplier}px`,
            maxHeight: '95vh',
            overflowY: 'auto'
          }}
        >
        
          {/* Location Header */}
          <div 
            style={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: `${14 * spacingMultiplier}px`,
              marginBottom: `${25 * spacingMultiplier}px`,
              fontSize: `${DASH.locationText}px`,
              fontWeight: '300',
              letterSpacing: '1.5px',
              color: 'rgba(255, 255, 255, 0.95)'
            }}
          >
            <MapPin size={DASH.locationIcon} style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))' }} />
            <span>{config.location}</span>
          </div>

          {/* Main Content - Split Layout */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1.2fr 1.8fr', 
            gap: `${RESPONSIVE.sectionGap * spacingMultiplier}px`, 
            marginBottom: `${30 * spacingMultiplier}px` 
          }}>
            
            {/* LEFT: Current Weather */}
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
              
              {effectiveDisplayOptions.animatedIcons && (
                <div 
                  style={{ 
                    marginBottom: `${20 * spacingMultiplier}px`,
                    filter: 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.3))',
                    animation: 'pulse 4s ease-in-out infinite'
                  }}
                >
                  {getWeatherIcon(weatherData.conditionCode, DASH.mainIcon)}
                </div>
              )}

              {effectiveDisplayOptions.showTemperature && (
                <div 
                  style={{ 
                    fontSize: `${DASH.mainTemp}px`,
                    fontWeight: '200',
                    color: '#ffffff',
                    lineHeight: '1',
                    marginBottom: `${20 * spacingMultiplier}px`,
                    textShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '-5px'
                  }}
                >
                  {Math.round(weatherData.temperature)}°
                </div>
              )}

              <div 
                style={{ 
                  fontSize: `${DASH.condition}px`,
                  color: 'rgba(255, 255, 255, 0.95)',
                  marginBottom: `${15 * spacingMultiplier}px`,
                  fontWeight: '300',
                  letterSpacing: '0.5px'
                }}
              >
                {translateWeatherCondition(weatherData.condition)}
              </div>

              {effectiveDisplayOptions.showFeelsLike && (
                <div 
                  style={{ 
                    fontSize: `${DASH.feelsLike}px`,
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontWeight: '300'
                  }}
                >
                  {t('weather.feelsLike', { temp: weatherData.feelsLike })}
                </div>
              )}

            </div>

            {/* RIGHT: Details Grid - 3 columns */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: `repeat(${RESPONSIVE.detailColumns}, 1fr)`, 
              gap: `${RESPONSIVE.detailGap * spacingMultiplier}px`, 
              alignContent: 'center' 
            }}>
              
              {effectiveDisplayOptions.showHumidity && (
                <DetailCard
                  icon={<Droplets size={RESPONSIVE.detailIcon} />}
                  value={`${weatherData.humidity}%`}
                  label={t('weather.humidity')}
                  valueSize={RESPONSIVE.detailValue}
                  labelSize={RESPONSIVE.detailLabel}
                  padding={RESPONSIVE.detailPadding}
                  fontMultiplier={1}
                  spacingMultiplier={spacingMultiplier}
                />
              )}

              {effectiveDisplayOptions.showWind && (
                <DetailCard
                  icon={<Wind size={RESPONSIVE.detailIcon} />}
                  value={`${weatherData.windSpeed} km/h`}
                  label={t('weather.wind')}
                  valueSize={RESPONSIVE.detailValue}
                  labelSize={RESPONSIVE.detailLabel}
                  padding={RESPONSIVE.detailPadding}
                  fontMultiplier={1}
                  spacingMultiplier={spacingMultiplier}
                />
              )}

              {effectiveDisplayOptions.showPressure && (
                <DetailCard
                  icon={<Gauge size={RESPONSIVE.detailIcon} />}
                  value={`${weatherData.pressure} hPa`}
                  label={t('weather.pressure')}
                  valueSize={RESPONSIVE.detailValue}
                  labelSize={RESPONSIVE.detailLabel}
                  padding={RESPONSIVE.detailPadding}
                  fontMultiplier={1}
                  spacingMultiplier={spacingMultiplier}
                />
              )}

              {effectiveDisplayOptions.showVisibility && (
                <DetailCard
                  icon={<Eye size={RESPONSIVE.detailIcon} />}
                  value={`${weatherData.visibility} km`}
                  label={t('weather.visibility')}
                  valueSize={RESPONSIVE.detailValue}
                  labelSize={RESPONSIVE.detailLabel}
                  padding={RESPONSIVE.detailPadding}
                  fontMultiplier={1}
                  spacingMultiplier={spacingMultiplier}
                />
              )}

              <DetailCard
                icon={<Sunrise size={RESPONSIVE.detailIcon} />}
                value={weatherData.sunrise}
                label={t('weather.sunrise')}
                valueSize={RESPONSIVE.detailValue}
                labelSize={RESPONSIVE.detailLabel}
                padding={RESPONSIVE.detailPadding}
                fontMultiplier={1}
                spacingMultiplier={spacingMultiplier}
              />

              <DetailCard
                icon={<Sunset size={RESPONSIVE.detailIcon} />}
                value={weatherData.sunset}
                label={t('weather.sunset')}
                valueSize={RESPONSIVE.detailValue}
                labelSize={RESPONSIVE.detailLabel}
                padding={RESPONSIVE.detailPadding}
                fontMultiplier={1}
                spacingMultiplier={spacingMultiplier}
              />

            </div>

          </div>

          {/* 5-Day Forecast */}
          {config.showForecast && weatherData.forecast && weatherData.forecast.length > 0 && (
            <div 
              style={{ 
                background: 'rgba(255, 255, 255, 0.06)',
                backdropFilter: 'blur(10px)',
                borderRadius: `${24 * spacingMultiplier}px`,
                padding: `${RESPONSIVE.forecastPadding * spacingMultiplier}px`,
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <div 
                style={{ 
                  fontSize: `${RESPONSIVE.forecastTitle}px`,
                  color: 'rgba(255, 255, 255, 0.9)',
                  marginBottom: `${20 * spacingMultiplier}px`,
                  fontWeight: '300',
                  letterSpacing: '0.5px'
                }}
              >
                {t('weather.forecast')}
              </div>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: `repeat(${RESPONSIVE.forecastColumns}, 1fr)`, 
                gap: `${RESPONSIVE.forecastGap * spacingMultiplier}px` 
              }}>
                {weatherData.forecast.slice(0, RESPONSIVE.forecastColumns).map((day, index) => (
                  <ForecastCard
                    key={index}
                    day={day}
                    daySize={RESPONSIVE.forecastDay}
                    emojiSize={RESPONSIVE.forecastEmoji}
                    tempSize={RESPONSIVE.forecastTempHigh}
                    tempLowSize={RESPONSIVE.forecastTempLow}
                    fontMultiplier={1}
                    spacingMultiplier={spacingMultiplier}
                  />
                ))}
              </div>
            </div>
          )}

        </div>

        <style>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }

          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }

          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
        `}</style>
      </div>
    );
  }

};

// 🎨 Helper Component: Detail Card
const DetailCard = ({ icon, value, label, valueSize = 52, labelSize = 22, padding = 40, fontMultiplier, spacingMultiplier }) => {
  return (
    <div 
      style={{ 
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
        borderRadius: `${28 * spacingMultiplier}px`,
        padding: `${padding * spacingMultiplier}px ${32 * spacingMultiplier}px`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: `${16 * spacingMultiplier}px`,
        border: '1px solid rgba(255, 255, 255, 0.15)',
        transition: 'all 0.3s ease',
        cursor: 'pointer'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
        e.currentTarget.style.transform = 'translateY(-5px)';
        e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.2)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ opacity: 0.9, filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))' }}>
        {icon}
      </div>
      <div style={{ 
        fontSize: `${valueSize * fontMultiplier}px`, 
        color: '#ffffff', 
        fontWeight: '500', 
        marginBottom: `${6 * spacingMultiplier}px` 
      }}>
        {value}
      </div>
      <div style={{ 
        fontSize: `${labelSize * fontMultiplier}px`, 
        color: 'rgba(255, 255, 255, 0.7)', 
        fontWeight: '300', 
        letterSpacing: '0.5px' 
      }}>
        {label}
      </div>
    </div>
  );
};

// 🎨 Helper Component: Forecast Card
const ForecastCard = ({ day, daySize = 24, emojiSize = 72, tempSize = 34, tempLowSize = 28, fontMultiplier, spacingMultiplier }) => {
  return (
    <div 
      style={{ 
        background: 'rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(8px)',
        borderRadius: `${24 * spacingMultiplier}px`,
        padding: `${28 * spacingMultiplier}px ${20 * spacingMultiplier}px`,
        textAlign: 'center',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        transition: 'all 0.3s ease',
        cursor: 'pointer'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
        e.currentTarget.style.transform = 'translateY(-8px)';
        e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.2)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ 
        fontSize: `${daySize * fontMultiplier}px`, 
        color: 'rgba(255, 255, 255, 0.8)', 
        marginBottom: `${16 * spacingMultiplier}px`, 
        fontWeight: '400', 
        letterSpacing: '0.5px' 
      }}>
        {day.day}
      </div>
      <div style={{ 
        fontSize: `${emojiSize * fontMultiplier}px`, 
        margin: `${16 * spacingMultiplier}px 0`, 
        filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2))' 
      }}>
        {day.icon}
      </div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'baseline', 
        gap: `${12 * spacingMultiplier}px`, 
        marginTop: `${16 * spacingMultiplier}px` 
      }}>
        <span style={{ fontSize: `${tempSize * fontMultiplier}px`, color: '#ffffff', fontWeight: '500' }}>{day.high}°</span>
        <span style={{ fontSize: `${tempLowSize * fontMultiplier}px`, color: 'rgba(255, 255, 255, 0.6)', fontWeight: '300' }}>{day.low}°</span>
      </div>
    </div>
  );
};

// 🎯 EXPORT PRESETS for use in Config component
export { DISPLAY_PRESETS };

export default WeatherWidget;